import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldExclamation,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineBolt,
} from "react-icons/hi2";
import { authService } from "../../services";
import hazardCategoryService from "../../services/hazardCategoryService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./HazardCategories.css";

const getCachedHazardCategoriesData = () => {
  try {
    const cachedCategories = sessionStorage.getItem("hazard_categories_data");
    return {
      categories: cachedCategories ? JSON.parse(cachedCategories) : [],
      hasCategories: !!cachedCategories,
    };
  } catch (e) {
    return { categories: [], hasCategories: false };
  }
};

const HazardCategories = () => {
  const cachedData = getCachedHazardCategoriesData();
  const [categories, setCategories] = useState(cachedData.categories);
  const [loading, setLoading] = useState(!cachedData.hasCategories);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);

  const [categoryFormData, setCategoryFormData] = useState({
    categoryCode: "",
    categoryName: "",
    description: "",
    isActive: true,
    displayOrder: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedHazardCategoriesData();
      if (refreshedCachedData.hasCategories) {
        setCategories(refreshedCachedData.categories);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to view categories");
      setLoading(false);
      return;
    }

    const hasCachedData = sessionStorage.getItem("hazard_categories_data");
    if (hasCachedData) {
      setLoading(false);
      loadCategories();
    } else {
      loadCategories();
    }
  }, [tenantId]);

  const loadCategories = async () => {
    try {
      const hasCachedData = sessionStorage.getItem("hazard_categories_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");
      const data = await hazardCategoryService.getCategories();
      setCategories(data || []);
      sessionStorage.setItem("hazard_categories_data", JSON.stringify(data || []));
    } catch (err) {
      setError("Failed to load categories. Please try again.");
      console.error("Error loading categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.categoryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.categoryCode?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      categoryCode: "",
      categoryName: "",
      description: "",
      isActive: true,
      displayOrder: categories.length,
    });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category, e) => {
    e.stopPropagation();
    setEditingCategory(category);
    setCategoryFormData({
      categoryCode: category.categoryCode || "",
      categoryName: category.categoryName || "",
      description: category.description || "",
      isActive: category.isActive !== undefined ? category.isActive : true,
      displayOrder: category.displayOrder || 0,
    });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id) => {
    try {
      await hazardCategoryService.deleteCategory(id);
      await loadCategories();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete category.");
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryFormData.categoryName.trim()) {
      setFormErrors({ categoryName: "Category name is required" });
      return;
    }

    try {
      setSubmitting(true);
      const data = { ...categoryFormData, tenantId };
      if (editingCategory) {
        await hazardCategoryService.updateCategory(editingCategory.categoryId, data);
      } else {
        await hazardCategoryService.createCategory(data);
      }
      await loadCategories();
      setShowCategoryModal(false);
    } catch (err) {
      setError("Failed to save category.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoading message="Loading hazard categories..." />;
  }

  return (
    <div className="pagecontainer hazard-categories">
      <div className="page-header">
        <div className="heder-contain">
          <h1>
            <HiOutlineShieldExclamation className="page-icon" />
            Hazard Categories
          </h1>
        </div>

        <div className="header-actions d-flex">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn-primary btn" onClick={handleAddCategory}>
            <HiOutlinePlus /> Add Category
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="categories-list">
        {filteredCategories.length === 0 ? (
          <div className="empty-subcategories">
            <p>No hazard categories found.</p>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.categoryId} className="category-list-card">
              <div className="category-main-content">
                <div className="category-icon-container">
                  <HiOutlineShieldExclamation />
                </div>
                <div className="category-text-content">
                  <div className="category-title-line">
                    <h3>{category.categoryName}</h3>
                    {category.categoryCode && (
                      <span className="category-code">
                        {category.categoryCode}
                      </span>
                    )}
                  </div>
                  {category.description && (
                    <p className="category-desc">
                      {category.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="category-actions-panel">
                <span
                  className={`status-badge ${category.isActive ? "active" : "inactive"}`}
                >
                  {category.isActive ? "Active" : "Inactive"}
                </span>
                <div className="action-buttons">
                  <button
                    className="btn-icon"
                    onClick={(e) => handleEditCategory(category, e)}
                    title="Edit Category"
                  >
                    <HiOutlinePencil />
                  </button>
                  <button
                    className="btn-icon delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({
                        type: "category",
                        id: category.categoryId,
                        name: category.categoryName,
                      });
                    }}
                    title="Delete Category"
                  >
                    <HiOutlineTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Category Modal */}
      <SharedModal
        isOpen={showCategoryModal}
        onClose={() => !submitting && setShowCategoryModal(false)}
        title={editingCategory ? "Edit Category" : "Add Category"}
        customWidth="550px"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCategoryModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" form="hazard-category-form" className="btn-primary" disabled={submitting}>
              {submitting
                ? "Saving..."
                : editingCategory
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="hazard-category-form" onSubmit={handleCategorySubmit}>
            <div className="form-group">
              <label>Category Code</label>
              <input
                type="text"
                value={categoryFormData.categoryCode}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    categoryCode: e.target.value,
                  })
                }
                placeholder="Enter category code"
              />
            </div>
            <div className="form-group">
              <label>
                Category Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={categoryFormData.categoryName}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    categoryName: e.target.value,
                  })
                }
                placeholder="Enter category name"
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={categoryFormData.displayOrder}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={categoryFormData.description}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    description: e.target.value,
                  })
                }
                rows={3}
                placeholder="Enter description"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="status-container">
                <label className="switch-styled">
                  <input
                    type="checkbox"
                    checked={categoryFormData.isActive}
                    onChange={(e) =>
                      setCategoryFormData({
                        ...categoryFormData,
                        isActive: e.target.checked,
                      })
                    }
                  />
                  <span className="slider-styled"></span>
                </label>
                <span className="status-text">
                  {categoryFormData.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        customClass="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              style={{ backgroundColor: "#ef4444", color: "white" }}
              onClick={() => handleDeleteCategory(deleteConfirm.id)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to deactivate the category{" "}
            <strong>{deleteConfirm?.name}</strong>?
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default HazardCategories;
